import React from 'react';
import { Box, useToken } from '@chakra-ui/react';
import {
  HStack,
  Pressable,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import {
  ADJUSTABLE_NUTRITION_BLOCKS,
  BLOCK_TYPE,
  BlockArchetype,
  UserBlock,
} from '../../../models/block';
import { getAssignedBy } from './utils';
import { CircleCheckIcon } from 'lucide-react';
import { EngagementAddon } from './EngagementAddon';

const laneBlockDetailConfig: {
  label: string;
  field: keyof UserBlock;
  color?: string;
}[] = [
  { label: 'Date assigned', field: 'date_assigned' },
  { label: 'Date archived', field: 'date_archived', color: 'red.500' },
];

const BlockInfoItem = ({
  label,
  value,
  defaultValue,
  color = 'blueGray.600',
}: {
  label: string;
  value?: string | number | null;
  defaultValue?: string;
  color?: string;
}) => {
  if (value || defaultValue)
    return (
      <Text color={color} fontSize={'xs'} mt={1}>
        {label}: {value || defaultValue}
      </Text>
    );

  return null;
};

export const LaneBlockCard = ({
  block,
  openEditBlockDrawer,
  openNutritionBlockDetailsDrawer,
  handleArchive,
  enableCompletedBanner,
}: {
  block: UserBlock;
  openNutritionBlockDetailsDrawer: (block: UserBlock) => void;
  openEditBlockDrawer: (block: UserBlock) => void;
  handleArchive: () => void;
  enableCompletedBanner?: boolean;
}) => {
  const [seafoam] = useToken('colors', ['seafoam']);

  const loggedGutChecks = block.metadata?.logged_gut_checks;
  const gutChecksTotal = block.metadata?.gut_checks_total;

  const showEngagementAddon =
    typeof loggedGutChecks === 'number' && typeof gutChecksTotal === 'number';

  const canOpenDetailsDrawer =
    !!block.id &&
    !!block.stable_string &&
    !!ADJUSTABLE_NUTRITION_BLOCKS.includes(block.stable_string);

  const canOpenEditDrawer =
    block.archetype === BlockArchetype.Goal ||
    block.block_type === BLOCK_TYPE.MindGut;

  const isClickable = canOpenDetailsDrawer || canOpenEditDrawer;
  const showCompletedBanner = !!enableCompletedBanner && !!block.date_ended;

  return (
    <VStack
      gap={0}
      w={'100%'}
      bgColor={'mint'}
      borderRadius={'xl'}
      borderWidth={1}
      mb={0}
    >
      {showCompletedBanner && (
        <HStack
          px={3}
          py={2}
          mb={0}
          alignItems={'center'}
          justifyContent={'space-between'}
          w={'100%'}
          gap={1}
        >
          <HStack gap={1} alignItems={'center'}>
            <CircleCheckIcon color={seafoam} size={16} />
            <Text fontSize={'sm'} color={'seafoam'} fontWeight={600}>
              Completed
            </Text>
          </HStack>
          <Pressable onClick={handleArchive} disabled={!!block.date_archived}>
            <Text fontSize={'sm'} fontWeight={600} color={'seafoam'}>
              Archive
            </Text>
          </Pressable>
        </HStack>
      )}
      <Box
        bgColor={'white'}
        borderColor={'cloud.200'}
        borderRadius={'xl'}
        borderWidth={1}
        m={'-1px'}
        overflow={'hidden'}
      >
        <Pressable
          w="100%"
          disabled={!isClickable}
          onClick={() => {
            if (canOpenDetailsDrawer) {
              openNutritionBlockDetailsDrawer(block);
              return;
            }
            if (canOpenEditDrawer) {
              openEditBlockDrawer(block);
            }
          }}
        >
          <Box p={3}>
            <Text
              color={'secondary.500'}
              fontSize={'md'}
              fontWeight={'normal'}
              textAlign={'left'}
            >
              {block.block_name}
            </Text>

            <BlockInfoItem
              label="Assigned By"
              value={getAssignedBy(block?.assigned_by)}
            />

            {laneBlockDetailConfig
              .filter(({ field }) => !!block[field])
              .map(({ label, field, color }) => (
                <BlockInfoItem
                  key={label}
                  color={color}
                  label={label}
                  value={block[field] as string | number}
                />
              ))}

            {!showEngagementAddon && (
              <BlockInfoItem
                label="Logged gut checks"
                value={block.metadata?.logged_gut_checks}
              />
            )}
          </Box>
        </Pressable>

        {showEngagementAddon && (
          <EngagementAddon
            mode="ofTotal"
            count={loggedGutChecks!}
            total={gutChecksTotal!}
            countNoun="Gut Checks"
          />
        )}
      </Box>
    </VStack>
  );
};

export default LaneBlockCard;
